import { Card } from '@components'
import Quill from 'quill'
import { type Component, createEffect, createSignal, onMount } from 'solid-js'
import { SolidQuill } from 'solid-quill'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Box, Grid } from 'styled-system/jsx'
import { useUi, useUpsert } from '../state'
import './input.css'
import { useMention } from '../state/useMention'
import { Mention } from './Mention'
import ReplyingTo from './ReplyingTo'
import Toolbar from './Toolbar'
import Voice from './Voice'
import { AudioBlot, MentionBlot, formats } from './quillFormatting'

export const Send: Component<{
	currentChannelId?: string
}> = (props) => {
	const { onSuccesfulSend, send, editingMessage, replyMessage } =
		useService(useUpsert)()
	const { isExpanded, setIsMentionOpen } = useService(useUi)()
	const { activeMentions } = useService(useMention)()
	const [isVoiceOpen, setIsVoiceOpen] = createSignal(false)
	let quill: Quill

	createEffect(() => {
		if (onSuccesfulSend()) {
			quill?.setText('')
		}
	})

	createEffect(() => {
		const message = editingMessage()
		if (message) {
			quill.root.innerHTML = message.content
		}
	})

	onMount(() => {
		if (Quill) {
			Quill.register({
				'formats/audio': AudioBlot,
				'blots/mention': MentionBlot,
			})
		}
	})

	const onSend = () => {
		const sanitizeHTML = (html: string) => {
			let sanitized = html
				.replace(/&nbsp;/g, ' ')
				.replace(/<(p|div)><br><\/\1>/g, '')
				.replace(/\s{2,}/g, ' ')
				.trim()

			if (!sanitized.startsWith('<') && sanitized !== '') {
				sanitized = `<p>${sanitized}</p>`
			}

			return sanitized
		}

		quill.update()

		const rawHTML = quill.getSemanticHTML()
		const cleanHTML = sanitizeHTML(rawHTML)

		if (props.currentChannelId && cleanHTML !== '<p></p>' && cleanHTML !== '') {
			send(cleanHTML, props.currentChannelId)
		}
	}

	const attachAudioToMessage = (audioUrl: string) => {
		if (!quill) return
		const range = quill.getSelection(true)
		const insertIndex = range ? range.index : quill.getLength()

		quill.insertText(insertIndex, '\n')
		quill.insertEmbed(insertIndex + 1, 'audio', audioUrl)
		quill.insertText(insertIndex + 2, '\n')
		setIsVoiceOpen(false)
	}

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === '@') {
			setIsMentionOpen(true)
		}

		if (e.ctrlKey && e.key === 'Enter') {
			e.preventDefault()
			onSend()
		}

		if (e.key === 'Backspace' && quill) {
			const selection = quill.getSelection()
			if (!selection || selection.length !== 0 || selection.index === 0) return

			const index = selection.index
			const [blot, offset] = quill.getLeaf(index - 1)
			if (!blot) return

			// Check if this leaf is inside a mention
			const node = blot.domNode as Node
			const mentionNode = node.parentElement?.closest('span.mention-blot')
			if (!mentionNode) return

			e.preventDefault()

			// Delete exactly the length of this blot
			const mentionStart = index - offset
			const mentionLength = blot.length()

			quill.deleteText(mentionStart - 1, mentionLength, 'user')

			// Optional: also delete a trailing space after the mention, if any
			const nextChar = quill.getText(mentionStart, 1)
			if (nextChar === ' ') {
				quill.deleteText(mentionStart, 1, 'user')
			}

			quill.setSelection(mentionStart, 0)
		}
	}

	createEffect(() => {
		const mentions = activeMentions()
		if (mentions.length && quill) {
			const mention = mentions[mentions.length - 1]
			let insertIndex = quill.getSelection(true)?.index ?? quill.getLength()

			const prevChar = quill.getText(insertIndex - 1, 1)
			if (prevChar === '@') {
				quill.deleteText(insertIndex - 1, 1, 'silent')
				insertIndex -= 1
			}

			if (mention.username) {
				quill.insertEmbed(insertIndex, 'mention', mention)
				quill.insertText(insertIndex + mention.username.length + 1, ' ')
				quill.formatText(insertIndex + mention.username.length + 1, 1, 'mention', false)
				quill.setSelection(insertIndex + mention.username.length + 2)
			}
		}
	})

	return (
		<Grid
			gridTemplateRows={replyMessage() ? 'auto 1fr' : '1fr'}
			mt={replyMessage() ? 0 : 2}
			gap={1}
		>
			<Mention />
			<ReplyingTo />
			<Card.Root>
				<Voice
					setAudio={attachAudioToMessage}
					isOpen={isVoiceOpen}
					setIsOpen={setIsVoiceOpen}
				/>
				<Box
					class={css({
						display: 'grid',
						gridTemplateRows: 'auto 1fr',
						height: '100%',
						gap: 2,
						p: 2,
						position: 'relative',
					})}
				>
					<Toolbar onSend={onSend} />
					<Grid
						gap={0}
						overflow={'auto'}
						class={css({
							maxHeight: !isExpanded() ? '90px' : 'auto',
						})}
					>
						<SolidQuill
							ref={(el) => (quill = el)}
							theme="snow"
							onKeyDown={onKeyDown}
							formats={[...formats, 'mention']}
							modules={{
								toolbar: {
									container: '#custom-toolbar',
									handlers: {
										undo: () => setIsVoiceOpen(true),
									},
								},
							}}
						/>
					</Grid>
				</Box>
			</Card.Root>
		</Grid>
	)
}
